import {IPolicyApplication, IPremium, IQuotes, ICustomer, IVehicle, IDriver, IClaim, IConviction} from '../models';
import * as moment from 'moment';
import * as document from './documents';
import { getIncludedResource } from '../util';
import { fromJsonApi as fromChannelJsonApi } from './channels';
import { isUndefined } from 'util';

const dateFromApi = (date) => {
  if (! date) {
    return '';
  }

  const parsedDate = moment(date).format('DD/MM/YYYY');

  return parsedDate !== 'Invalid Date' ? parsedDate : '';
};

const dateToApi = (date) => {
  if (! date) {
    return '';
  }

  const formattedDate = moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
    // .utcOffset(0, true).format();
  return formattedDate.toLowerCase() !== 'invalid date' ? formattedDate : '';
};

export function fromJsonApi(data, included = []): IPolicyApplication {
  const { id, attributes : { metadata } } = data;

  const premiums: IPremium[] = getIncludedResource(data, included, 'premiums', true).map((premium): IPremium => {
    let scheme = getIncludedResource(premium, included, 'scheme');
    if (scheme === null) {
      scheme = getIncludedResource(data, included, 'schemes');
    }

    const documentsResource = getIncludedResource(data, included, 'channel_scheme_documents', true);
    const documents = {};
    documentsResource.forEach((document) => {
      documents[document.attributes.name] = {
        path: document.attributes.file_path,
        displayName: document.attributes.display_name,
        type: document.attributes.type,
      };
    });

    return {
      id: premium.id,
      schemeType: premium.attributes.scheme.scheme_type,
      calculatedAt: premium.attributes.calculated_at,
      validUntil: premium.attributes.valid_until,
      currency: premium.attributes.currency,
      tax: premium.attributes.tax,
      taxRate: premium.attributes.tax_rate,
      exTax: premium.attributes.exTax,
      principal: premium.attributes.principal,
      grossExAdditional: premium.attributes.gross_ex_additional,
      stampDuty: premium.attributes.additional_tax_rates,
      gross: premium.attributes.gross,
      benefits: premium.attributes.benefits.map((benefit) => ({
        name: benefit.name,
        text: benefit.text,
        description: benefit.description,
        inverse: benefit.inverse,
        excess: benefit.excess,
        displayName: benefit.display_name,
        shortName: benefit.short_name,
        subBenefits: benefit.sub_benefits && benefit.sub_benefits.map((subBenefit) => ({
          excess: subBenefit.excess,
          name: subBenefit.name,
          text: subBenefit.text,
        })),
      })),
      information: premium.attributes.information && {
        fees: premium.attributes.information.fees,
        standardExcess: premium.attributes.information.standard_excess,
        boltOns: premium.attributes.information.bolt_ons,
        upgrade: premium.attributes.information.upgrade && {
          schemeId: premium.attributes.information.upgrade.scheme,
          amount: premium.attributes.information.upgrade.amount,
          coverLevel: premium.attributes.information.upgrade.coverLevel,
        },
        maxTripDuration: premium.attributes.information.max_trip_duration,
        startDate: premium.attributes.information.start_date,
        region: premium.attributes.information.region,
        tripType: premium.attributes.information.scheme_type,
        breakdownCover: {
          included: premium.attributes.information.horsebox_breakdown_included ?
            premium.attributes.information.horsebox_breakdown_included : [],
          gross: premium.attributes.information.horsebox_breakdown_ap_gross ?
            premium.attributes.information.horsebox_breakdown_ap_gross : [],
          tax: premium.attributes.information.horsebox_breakdown_ap_tax ?
            premium.attributes.information.horsebox_breakdown_ap_tax : [],
          value: premium.attributes.information.horsebox_breakdown_value_gross ?
            premium.attributes.information.horsebox_breakdown_value_gross : [],
        },
      },
      declines: premium.attributes.declines,
      referrals: premium.attributes.referrals,
      errors: premium.attributes.errors,
      scheme: {
        id: scheme.id,
        coverLevel: scheme.attributes.cover_level,
        status: scheme.attributes.status,
        logo: scheme.attributes.logo,
        name: scheme.attributes.name,
        insurer: scheme.attributes.insurer,
        documents,
        metadata: scheme.attributes.metadata,
      },
    };
  });

  const mapClaim = (claim: any): IClaim => {
    const data: IClaim = {
      date: dateFromApi(claim.date),
      type: claim.type,
      cost: claim.ad_cost,
      fault: claim.fault,
      open: claim.open,
    };
    return data;
  };

  const mapConviction = (conviction: any): IConviction => {
    const data: IConviction = {
      code: conviction.code,
      description: conviction.description,
      date: dateFromApi(conviction.date),
      points: conviction.points,
      banned: conviction.banned,
      fine: conviction.fine,
    };
    return data;
  };

  const customer: ICustomer = getIncludedResource(data, included, 'customer', true).map((customer): ICustomer => {
    const claims = [];
    if (customer.attributes.metadata.claims) {
      customer.attributes.metadata.claims.forEach((claim) => {
        claims.push(mapClaim(claim));
      });
    }

    const convictions = [];
    if (customer.attributes.metadata.convictions) {
      customer.attributes.metadata.convictions.forEach((conviction) => {
        claims.push(mapConviction(conviction));
      });
    }

    return {
      id: customer.id,
      title: customer.attributes.title,
      firstName: customer.attributes.first_name,
      lastName: customer.attributes.last_name,
      customerName: customer.attributes.customer_name,
      companyName: customer.attributes.company_name,
      dob: dateFromApi(customer.attributes.dob),
      address: customer.attributes.address,
      email: customer.attributes.email,
      phone1: customer.attributes.phone1,
      status: customer.attributes.status,
      deceased: customer.attributes.deceased,
      metadata: {
        status: customer.attributes.metadata.status,
        microEnterprise: customer.attributes.metadata.micro_enterprise,
        tradingName: customer.attributes.metadata.trading_name,
        companyRegistrationNumber: customer.attributes.metadata.company_registration_number,
        year: customer.attributes.metadata.year,
        typeOfLicence: customer.attributes.metadata.type_of_licence,
        lengthOfLicence: dateFromApi(customer.attributes.metadata.length_of_licence),
        claims,
        convictions,
      },
      policies: customer.policies,
      policyApplications: customer.policy_applications,
    };
  });

  const quotes: IQuotes = {};

  premiums.forEach((premium) => {
    if (!quotes[premium.scheme.coverLevel]) {
      quotes[premium.scheme.coverLevel] = {};
    }

    quotes[premium.scheme.coverLevel][premium.schemeType] = premium;
  });

  const quotePremiums = getIncludedResource(data, included, 'policies/premiums', true)
    .map((item) => quotePremiums.fromJsonApi(item, included));

  const documents = getIncludedResource(data, included, 'documents', true)
    .map((item) => document.fromJsonApi(item, included));

  const channel = ((channel) => {
    if (! channel) {
      return;
    }

    return fromChannelJsonApi(channel, included);
  })(getIncludedResource(data, included, 'channel'));

  const mapVehicle = (vehicle: any): IVehicle => {
    const data: IVehicle = {
      id: vehicle.id,
      bodyType: vehicle.body_type,
      year: vehicle.year,
      value: vehicle.value,
      roadRegistered: vehicle.road_registered,
      registrationNumber: vehicle.registration_number,
      make: vehicle.make,
      model: vehicle.model,
      weight: vehicle.weight,
      drivingRestriction: vehicle.driving_restriction,
      drivingRestrictionCalculated: vehicle.driving_restriction_calculated,
      drivers: vehicle.drivers,
      showExtendedUse: vehicle.horsebox_scheme.show_extended_use,
      extendedUses: vehicle.additional_use,
      equineTradeExtendedUsers: vehicle.horsebox_scheme.equine_trade_extended_users,
      nonEquineTradeExtendedUsers: vehicle.horsebox_scheme.non_equine_trade_extended_users,
      greenCard: vehicle.green_card,
      mileage: vehicle.mileage,
      proposerOwner: vehicle.proposer_owner,
      owner: vehicle.owner,
      ownerName: vehicle.owner_name,
      proposerOvernight: vehicle.proposer_overnight,
      postcode: vehicle.postcode,
      coverType: vehicle.cover_type,
      tracker: vehicle.tracker,
      breakdownCover: vehicle.horsebox_scheme.breakdown_cover,
      age: vehicle.age,
      use: vehicle.use,
      ncd: vehicle.ncd,
      excessTheftLockedBuilding: vehicle.excess_theft_locked_building,
      excessTheftOther: vehicle.excess_theft_other,
      excessAdft: vehicle.excess_adft,
    };
    return data;
  };

  const vehicles = [];
  if (metadata.vehicles.horseboxes) {
    metadata.vehicles.horseboxes.forEach((vehicle) => {
      vehicles.push(mapVehicle(vehicle));
    });
  }

  const mapDriver = (driver: any): IDriver => {
    const driverClaims = [];
    if (driver.claims) {
      driver.claims.forEach((claim) => {
        driverClaims.push(mapClaim(claim));
      });
    }

    const driverConvictions = [];
    if (driver.convictions) {
      driver.convictions.forEach((conviction) => {
        driverConvictions.push(mapConviction(conviction));
      });
    }

    const data: IDriver = {
      id: driver.id,
      title: driver.title,
      firstName: driver.first_name,
      lastName: driver.last_name,
      dob: dateFromApi(driver.dob),
      proposer: driver.proposer,
      typeOfLicence: driver.type_of_licence,
      lengthOfLicence: dateFromApi(driver.length_of_licence),
      nonEquineTrade: driver.non_equine_trade,
      vehicles: driver.vehicles,
      claims: driverClaims,
      convictions: driverConvictions,
    };
    return data;
  };

  const drivers = [];
  if (metadata.drivers) {
    metadata.drivers.forEach((driver) => {
      drivers.push(mapDriver(driver));
    });
  }

  const claims = [];
  if (metadata.insured.metadata.claims) {
    metadata.insured.metadata.claims.forEach((claim) => {
      claims.push(mapClaim(claim));
    });
  }

  const convictions = [];
  if (metadata.insured.metadata.convictions) {
    metadata.insured.metadata.convictions.forEach((conviction) => {
      convictions.push(mapConviction(conviction));
    });
  }

  // noinspection UnnecessaryLocalVariableJS
  const policyApplication: IPolicyApplication = {
    quotationTypes: metadata.quotation_types,
    startDate: dateFromApi(metadata.start_date),
    endDate: metadata.end_date,
    quotes: metadata.quotes,
    // declarationQuestion: metadata.declaration_question,
    steps: metadata.steps,
    schemeId: metadata.scheme_id,
    quoteReference: data.attributes.quote_reference,
    status: data.attributes.status ?
      data.attributes.status
      : null,
    quotePremiums,
    premiums,
    generateDocuments: false,
    emailSend: false,
    documents,
    channel,
    calculateAllSchemes: false,
    customer,
    renewingPolicyNumber: data.attributes.renewing_policy_number,
    renewal: data.attributes.quote_type === 'renewal',
    quoteType: data.attributes.quote_type,

    backdatedCover: metadata.backdated_cover === 'true' ? true : false,
    insured: {
      id: metadata.insured.id,
      title: metadata.insured.title,
      firstName: metadata.insured.first_name,
      lastName: metadata.insured.last_name,
      email: metadata.insured.email,
      dob: dateFromApi(metadata.insured.dob),
      companyName: metadata.insured.company_name,
      metadata: {
        status: metadata.insured.metadata.status,
        microEnterprise: metadata.insured.metadata.micro_enterprise,
        tradingName: metadata.insured.metadata.trading_name,
        companyRegistrationNumber: metadata.insured.metadata.company_registration_number,
        year: metadata.insured.metadata.year,
        typeOfLicence: metadata.insured.metadata.type_of_licence,
        lengthOfLicence: dateFromApi(metadata.insured.metadata.length_of_licence),
        claims,
        convictions,
      },
      address: metadata.insured.address,
      phone1: metadata.insured.phone1,
    },

    declaration: metadata.declaration,
    declarationTradeVehicle: metadata.declaration_trade_vehicle,
    vehicleCount: metadata.vehicle_count.horseboxes,
    vehicles,
    personalCustomer: metadata.personal_customer,
    customerType: metadata.customer_type,
    tenure: metadata.tenure,
    voluntaryExcess: metadata.voluntary_excess,
    voluntaryExcessEnabled: metadata.voluntary_excess > 0 ? 'true' : (metadata.voluntary_excess === '0' ? 'false' : ''),
    driverCount: metadata.driver_count,
    drivers,
    quickQuote: !isUndefined(metadata.quick_quote) ? metadata.quick_quote : false,
    contractingPercentage: !isUndefined(metadata.contracting_percentage) ? metadata.contracting_percentage :
      '1% - 10%',
    nonEquineTrade: metadata.non_equine_trade,
    enrichment: metadata.enrichment,

    excessOverrides: metadata.excess_overrides,
    rateOverrides: metadata.rate_overrides,
    compulsoryExcess: metadata.compulsory_excess,

    paymentSchedule: metadata.payment_schedule,
    ignoreReferrals: metadata.ignore_referrals,

    postcodeReferrals: {
      decline: metadata.postcode_referrals ? metadata.postcode_referrals.decline : null,
      refer: metadata.postcode_referrals ? metadata.postcode_referrals.refer : null,
    },
    vehicleReferrals: {
      bodyTypeRefer: metadata.vehicle_referrals ? metadata.vehicle_referrals.body_type_refer : null,
      bodyTypeDecline: metadata.vehicle_referrals ? metadata.vehicle_referrals.body_type_decline : null,
      vehicleAge: metadata.vehicle_referrals ? metadata.vehicle_referrals.vehicle_age : null,
      vehicleValue: metadata.vehicle_referrals ? metadata.vehicle_referrals.vehicle_value : null,
    },
    tempDriver: {
      title: '',
      firstName: '',
      lastName: '',
      hasNoNameYet: false,
    },
  };

  policyApplication.insured.emailConfirmation = metadata.insured.email;

  if (id) {
    policyApplication.id = id;
  }

  if (quotes) {
    policyApplication.quotes = quotes;
  }

  return policyApplication;
}

export function toJsonApi(policyApplication: IPolicyApplication, channelId?: string, brokerId?: string) {

  const mapClaim = () => (claim: IClaim) => {
    const data: any = {
      date: dateToApi(claim.date),
      type: claim.type,
      ad_cost: claim.cost,  // TODO what about AD/TP split?
      fault: claim.fault,
      open: claim.open,
    };
    return data;
  };

  const mapConviction = () => (conviction: IConviction) => {
    const data: any = {
      code: conviction.code,
      description: conviction.description,
      date: dateToApi(conviction.date),
      points: conviction.points,
      banned: conviction.banned,
      fine: conviction.fine,
    };
    return data;
  };

  const mapVehicle = () => (vehicle: IVehicle) => {
    const data: any = {
      id: vehicle.id,
      body_type: vehicle.bodyType,
      year: vehicle.year,
      value: vehicle.value,
      road_registered: vehicle.roadRegistered,
      registration_number: vehicle.registrationNumber,
      make: vehicle.make,
      model: vehicle.model,
      weight: vehicle.weight,
      driving_restriction: vehicle.drivingRestriction,
      driving_restriction_calculated: vehicle.drivingRestrictionCalculated,
      drivers: vehicle.drivers,
      additional_use: vehicle.extendedUses,
      horsebox_scheme: {
        show_extended_use: vehicle.showExtendedUse,
        breakdown_cover: vehicle.breakdownCover,
        equine_trade_extended_users: vehicle.equineTradeExtendedUsers,
        non_equine_trade_extended_users: vehicle.nonEquineTradeExtendedUsers,
      },
      green_card: vehicle.greenCard,
      mileage: vehicle.mileage,
      proposer_owner: vehicle.proposerOwner,
      owner: vehicle.owner,
      owner_name: vehicle.ownerName,
      proposer_overnight: vehicle.proposerOvernight,
      postcode: vehicle.postcode,
      cover_type: vehicle.coverType,
      tracker: vehicle.tracker,
      age: vehicle.age,
      use: vehicle.use,
      ncd: vehicle.ncd,
      excess_theft_locked_building: vehicle.excessTheftLockedBuilding,
      excess_theft_other: vehicle.excessTheftOther,
      excess_adft: vehicle.excessAdft,
    };
    return data;
  };

  const mapDriver = () => (driver: IDriver) => {
    const data: any = {
      id: driver.id,
      title: driver.title,
      first_name: driver.firstName,
      last_name: driver.lastName,
      dob: dateToApi(driver.dob),
      proposer: driver.proposer,
      type_of_licence: driver.typeOfLicence,
      length_of_licence: dateToApi(driver.lengthOfLicence),
      non_equine_trade: driver.nonEquineTrade,
      vehicles: [],
      claims: driver.claims ? driver.claims.map(mapClaim()) : [],
      convictions: driver.convictions ? driver.convictions.map(mapConviction()) : [],
    };
    return data;
  };

  const metadata: any = {
    quotation_types: policyApplication.quotationTypes,
    channel: 'grnhorse-b2c',
    horsebox_scheme: true,
    start_date: dateToApi(policyApplication.startDate),
    end_date: policyApplication.endDate,
    scheme_id: policyApplication.schemeId,
    policy_declaration: policyApplication.policyDeclaration,
    generate_documents: policyApplication.generateDocuments,
    email_send: policyApplication.emailSend,
    steps: policyApplication.steps,

    backdated_cover: policyApplication.backdatedCover,
    insured: {
      title: policyApplication.insured.title,
      first_name: policyApplication.insured.firstName,
      last_name: policyApplication.insured.lastName,
      email: policyApplication.insured.email,
      dob: dateToApi(policyApplication.insured.dob),
      company_name: policyApplication.insured.companyName,
      metadata: {
        status: policyApplication.insured.metadata.status,
        micro_enterprise: policyApplication.insured.metadata.microEnterprise,
        trading_name: policyApplication.insured.metadata.tradingName,
        company_registration_number: policyApplication.insured.metadata.companyRegistrationNumber,
        year: policyApplication.insured.metadata.year,
        type_of_licence: policyApplication.insured.metadata.typeOfLicence,
        length_of_licence: dateToApi(policyApplication.insured.metadata.lengthOfLicence),
        claims: policyApplication.insured.metadata.claims ?
          policyApplication.insured.metadata.claims.map(mapClaim()) : [],
        convictions: policyApplication.insured.metadata.convictions ?
          policyApplication.insured.metadata.convictions.map(mapConviction()) : [],
      },
      address: policyApplication.insured.address,
      phone1: policyApplication.insured.phone1,
    },

    declaration: policyApplication.declaration,
    declaration_trade_vehicle: policyApplication.declarationTradeVehicle,
    vehicle_count: {
      agricultural: 0,
      cars: 0,
      commercial: 0,
      horseboxes: policyApplication.vehicleCount,
      trailers: 0,
    },
    vehicles: {
      agricultural: [],
      cars: [],
      commercial: [],
      horseboxes: policyApplication.vehicles ? policyApplication.vehicles.map(mapVehicle()) : [],
      trailers: [],
    },
    personal_customer: policyApplication.personalCustomer,
    customer_type: policyApplication.customerType,
    tenure: policyApplication.tenure,
    voluntary_excess: policyApplication.voluntaryExcess,
    driver_count: policyApplication.driverCount,
    drivers: policyApplication.drivers ? policyApplication.drivers.map(mapDriver()) : [],
    quick_quote: policyApplication.quickQuote,
    contracting_percentage: policyApplication.contractingPercentage,
    non_equine_trade: policyApplication.nonEquineTrade,
    enrichment: policyApplication.enrichment,

    excess_overrides: policyApplication.excessOverrides,
    rate_overrides: policyApplication.rateOverrides,
    compulsory_excess: policyApplication.compulsoryExcess,

    payment_schedule: policyApplication.paymentSchedule,

    ignore_referrals: policyApplication.ignoreReferrals,

    postcode_referrals: {
      decline: policyApplication.postcodeReferrals ? policyApplication.postcodeReferrals.decline : false,
      refer: policyApplication.postcodeReferrals ? policyApplication.postcodeReferrals.refer : false,
    },
    vehicle_referrals: {
      body_type_refer: policyApplication.vehicleReferrals ? policyApplication.vehicleReferrals.bodyTypeRefer : false,
      body_type_decline: policyApplication.vehicleReferrals ? policyApplication.vehicleReferrals.bodyTypeDecline :
        false,
      vehicle_age: policyApplication.vehicleReferrals ? policyApplication.vehicleReferrals.vehicleAge : false,
      vehicle_value: policyApplication.vehicleReferrals ? policyApplication.vehicleReferrals.vehicleValue : false,
    },
  };

  if (!metadata.insured.first_name && !metadata.insured.company_name) {
    metadata.insured.first_name = 'Test';
  }
  if (!metadata.insured.last_name && !metadata.insured.company_name) {
    metadata.insured.last_name = 'Test';
  }

  const meta = {
    customer: {
      create_account: policyApplication.createAccount ? policyApplication.createAccount : false,
      password: policyApplication.password,
      password_confirmation: policyApplication.passwordConfirmation,
    },
    payment_type: policyApplication.paymentType,
  };

  // noinspection UnnecessaryLocalVariableJS
  const data: any = {
    type: 'application',
    attributes: {
      metadata,
      quote_reference: policyApplication.quoteReference,
      quote_type: policyApplication.quoteType,
      policy_number: policyApplication.renewingPolicyNumber,
    },
    meta,
    relationships: {},
  };

  if (policyApplication.schemeId && ! policyApplication.calculateAllSchemes) {
    data.relationships.schemes = {
      data: [
        { id: policyApplication.schemeId },
      ],
    };
  }

  if (policyApplication.id) {
    data.id = policyApplication.id;
  }

  if (channelId) {
    data.relationships.channel = {
      data: {
        id: channelId,
      },
    };
  }

  if (brokerId) {
    data.relationships.broker = {
      data: {
        id: brokerId,
      },
    };
  }

  return data;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/data/api/transformers/application.ts